/**
 * The Wire: every call this tab makes or is told about, and every raw frame
 * its sockets hear, one line each, filterable by where it came from.
 *
 * The Wire is the one place a person reads the traffic as it was, so it is
 * also where secrets are scrubbed: nothing that looks like a key, a signing
 * secret, a verify token or a watch ticket's nonce is kept, and a `secret`
 * field is stored as "(shown once)". The user's own Anthropic key reads
 * `sk-ant-…••••` wherever it would have been. The capsule that shows a new
 * secret is the only thing that ever holds it, and only until it is copied or
 * closed.
 */

import { h, mount as put } from '../../../console/src/dom.ts';
import type {
  Answered, AppContext, CallRecord, Json, Link, LinkState, PresenceOut, Screen, Source, TapEvent, Twins, Unsub, WorldDescribe, WorldEvent,
} from '../contracts.ts';
import { isObj } from '../model/json.ts';
import { ANTHROPIC_HINT, ANTHROPIC_SECRET } from '../spec/twins.ts';

// -- redaction ---------------------------------------------------------------------------------

const KEY_RE = /(sbk|sbo|sbv)_[A-Za-z0-9_-]+/g;
const WHSEC_RE = /whsec_[A-Za-z0-9_-]+/g;
const TICKET_RE = /(wt_[a-z0-9][a-z0-9-]{0,62}\.[a-z0-9][a-z0-9._-]{0,62}\.)[0-9a-f]{32}/g;

/** Would this text leak a credential? True for anything the redactor would have changed. */
export const leaks = (text: string): boolean =>
  /(sbk|sbo|sbv|whsec)_[A-Za-z0-9]/.test(text) || /wt_[^\s"]*\.[0-9a-f]{32}/.test(text) || /sk-ant-[A-Za-z0-9]/.test(text);

/** Text with every credential shape masked; `known` are this tab's own keys, masked wherever they appear. */
export const redactText = (text: string, known: readonly (string | null | undefined)[] = []): string => {
  let out = text;
  for (const k of known) if (k && k.length >= 8) out = out.split(k).join('••••');
  return out.replace(KEY_RE, '$1_…••••').replace(WHSEC_RE, 'whsec_…••••').replace(TICKET_RE, '$1••••').replace(ANTHROPIC_SECRET, ANTHROPIC_HINT);
};


/** An Authorization value as it may be shown: the scheme and the key's family, never the key. */
export const redactAuthorization = (value: string): string => {
  const m = /^(\S+)\s+(sbk|sbo|sbv)_/.exec(value);
  if (m) return `${m[1]} ${m[2]}_…••••`;
  const scheme = /^(\S+)\s+\S/.exec(value);
  return scheme ? `${scheme[1]} ••••` : '••••';
};

/** JSON with secrets replaced: `secret` fields become "(shown once)", credentials inside strings are masked. */
export const redactJson = (value: Json, known: readonly (string | null | undefined)[] = []): Json => {
  if (typeof value === 'string') return redactText(value, known);
  if (Array.isArray(value)) return value.map((v) => redactJson(v, known));
  if (!isObj(value)) return value;
  const out: { [k: string]: Json } = {};
  for (const [k, v] of Object.entries(value)) {
    const key = k.toLowerCase();
    if (key === 'secret' && typeof v === 'string') out[k] = '(shown once)';
    else if (key === 'authorization' && typeof v === 'string') out[k] = redactAuthorization(v);
    else if ((key === 'turnstiletoken' || key === 'authorization_token') && typeof v === 'string') out[k] = '(hidden)';
    // The user's own Anthropic key, whatever was typed there: never shown, only that it was sent.
    else if (key === 'anthropickey' && typeof v === 'string') out[k] = ANTHROPIC_HINT;
    else out[k] = redactJson(v, known);
  }
  return out;
};

/** Twins as they may be shown: headers and bodies scrubbed, curl masked (it should only say $SB_KEY anyway). */
export const redactTwins = (twins: Twins, known: readonly (string | null | undefined)[] = []): Twins => {
  const headers: Record<string, string> = {};
  for (const [k, v] of Object.entries(twins.rest.headers)) {
    headers[k] = k.toLowerCase() === 'authorization' ? redactAuthorization(v) : redactText(v, known);
  }
  return {
    rest: {
      method: twins.rest.method,
      url: redactText(twins.rest.url, known),
      headers,
      ...(twins.rest.body === undefined ? {} : { body: redactJson(twins.rest.body, known) }),
    },
    curl: redactText(twins.curl, known),
    mcp: twins.mcp === null ? null : redactJson(twins.mcp, known),
  };
};

/** A call record as the log keeps it: the same call, nothing secret left in it. */
export const scrubRecord = (record: CallRecord, known: readonly (string | null | undefined)[] = []): CallRecord => ({
  id: record.id,
  op: record.op,
  args: redactJson(record.args as Json, known) as CallRecord['args'],
  persona: record.persona,
  source: record.source,
  origin: record.origin,
  via: record.via,
  twins: redactTwins(record.twins, known),
  sentAt: record.sentAt,
  answer: record.answer === null ? null : { ...record.answer, body: redactJson(record.answer.body, known) },
});

// -- what an answer says, whichever road it took ------------------------------------------------

/** The operation's own status and body, whether the call went as REST or as MCP tools/call (the envelope unwrapped). */
export const outcomeOf = (record: Pick<CallRecord, 'answer'>): { status: number; body: Json } | null => {
  const a = record.answer;
  if (!a) return null;
  const b = a.body;
  if (isObj(b) && b.jsonrpc === '2.0' && isObj(b.result) && isObj(b.result.structuredContent)) {
    const sc = b.result.structuredContent;
    return { status: typeof sc.status === 'number' ? sc.status : a.status, body: sc.body ?? null };
  }
  return { status: a.status, body: b };
};

export type Tone = 'pending' | 'ok' | 'refused' | 'error' | 'info';

export const toneOf = (status: number | null): Tone =>
  status === null ? 'pending' : status === 0 ? 'error' : status < 300 ? 'ok' : status === 409 ? 'refused' : 'error';

export interface AnswerView {
  /** The operation's status: an MCP tools/call's own, not its envelope's 200. */
  readonly status: number;
  readonly body: Json;
  readonly tone: Tone;
  /** The problem's detail, or the JSON-RPC error, in a line. */
  readonly detail: string;
  /** The HTTP status of the MCP envelope, when the call went as tools/call and it says something else. */
  readonly mcp: number | null;
  /** The MCP server answered with a JSON-RPC error: the operation was never reached, so `status` is only the envelope's. */
  readonly rpc: boolean;
}

/** An answer as every panel shows it: the verdict of the operation, with the transport's status kept secondary. */
export const answerOf = (answer: Answered, via: CallRecord['via'] = 'rest'): AnswerView => {
  const out = outcomeOf({ answer }) ?? { status: answer.status, body: answer.body };
  const b = answer.body;
  const rpcError = via === 'mcp' && isObj(b) && b.jsonrpc === '2.0' && isObj(b.error) ? b.error : null;
  const detail = rpcError
    ? `JSON-RPC ${typeof rpcError.code === 'number' ? rpcError.code : ''} ${typeof rpcError.message === 'string' ? rpcError.message : ''}`.replace(/\s+/g, ' ').trim()
    : out.status >= 400 && isObj(out.body) && typeof out.body.detail === 'string' ? out.body.detail : '';
  return {
    status: out.status,
    body: out.body,
    tone: rpcError ? 'error' : toneOf(out.status),
    detail,
    mcp: via === 'mcp' && (rpcError || out.status !== answer.status) ? answer.status : null,
    rpc: rpcError !== null,
  };
};

// -- secrets shown once ---------------------------------------------------------------------------
//
// Only the caller of a call ever sees its secret: the Client gives its listeners (the Wire, the
// recorder, any panel listening) a scrubbed copy, with "(shown once)" where the secret was. So a
// secret is shown by whoever fired the call, from the record `Actions.run` hands back; a
// listener can never show one, and must never take the placeholder for a key.

/** A secret as the API mints them (a key, an operator key, a verify token, a webhook's signing secret); never a placeholder or a masked one. */
export const SECRET_SHAPE = /^(?:sbk|sbo|sbv|whsec)_[A-Za-z0-9_-]+$/;
const KEY_SHAPE = /^sbk_[A-Za-z0-9_-]+$/;

/** The key this tab runs on, from `whoami` (the `me` record). */
export const ownKeyId = (me: Json | undefined): string | null => (isObj(me) && isObj(me.key) && typeof me.key.id === 'string' ? me.key.id : null);

/** A secret the answer carries, whatever made it: `secret`, or `key.secret` (a verify or an operator mint). */
export const secretOf = (record: CallRecord): { secret: string; what: string } | null => {
  const a = outcomeOf(record);
  if (!a || a.status < 200 || a.status >= 300 || !isObj(a.body)) return null;
  const b = a.body;
  const s = (v: Json | undefined): string | null => (typeof v === 'string' ? v : null);
  if (typeof b.secret === 'string' && SECRET_SHAPE.test(b.secret)) {
    const what = typeof b.url === 'string' ? `signing secret for ${b.url}` : `key ${s(b.id) ?? ''}${s(b.label) ? ` (${s(b.label)})` : ''}`.trim();
    return { secret: b.secret, what };
  }
  if (isObj(b.key) && typeof b.key.secret === 'string' && SECRET_SHAPE.test(b.key.secret)) {
    return { secret: b.key.secret, what: `key ${s(b.key.id) ?? ''} for ${s(b.workspace) ?? 'the workspace'}`.trim() };
  }
  return null;
};

export type SessionEffect = { readonly kind: 'ended'; readonly id: string } | { readonly kind: 'rotated'; readonly id: string; readonly from: string; readonly secret: string };

/** What an answer means for this tab's own key: rotated (move to the successor) or revoked (end the session). */
export const sessionEffectOf = (record: CallRecord, own: string | null): SessionEffect | null => {
  const a = outcomeOf(record);
  if (!own || record.persona !== 'A' || !a || a.status < 200 || a.status >= 300 || !isObj(a.body)) return null;
  const b = a.body;
  // A listener's copy says "(shown once)" where the successor's secret was: that is never a key to move to.
  if (isObj(b.retired) && b.retired.id === own && typeof b.secret === 'string' && KEY_SHAPE.test(b.secret) && typeof b.id === 'string') {
    return { kind: 'rotated', id: b.id, from: own, secret: b.secret };
  }
  if (b.id === own && b.state === 'revoked') return { kind: 'ended', id: own };
  return null;
};

/** Shown once: the only element that ever holds a secret, and it takes itself out of the page on copy or close. */
export const secretCapsule = (doc: Document, secret: string, what: string, onGone: () => void): HTMLElement => {
  let value: string | null = secret;
  const code = h('code', { class: 'acct-secret' }, secret);
  const gone = (): void => {
    value = null;
    code.textContent = '';
    capsule.remove();
    onGone();
  };
  const copy = h('button', { class: 'acct-btn hot', type: 'button' }, 'copy and close');
  // Without a clipboard (or when it refuses), the secret stays and is selected for ⌘C: it is never thrown away unseen.
  const manual = (): void => {
    const range = doc.createRange();
    range.selectNodeContents(code);
    const sel = doc.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    copy.textContent = 'selected: press ⌘C, then close';
  };
  copy.addEventListener('click', () => {
    const v = value;
    if (v === null) return;
    const clip = doc.defaultView?.navigator.clipboard;
    if (!clip) { manual(); return; }
    clip.writeText(v).then(gone, manual);
  });
  const close = h('button', { class: 'acct-btn', type: 'button', 'aria-label': 'close; the secret cannot be shown again' }, 'close');
  close.addEventListener('click', gone);
  const capsule = h('div', { class: 'acct-capsule', role: 'alert' },
    h('span', { class: 'acct-eyebrow' }, 'SHOWN ONCE'),
    h('p', { class: 'acct-capsule-what' }, what),
    code,
    h('p', { class: 'acct-note' }, 'Copy it now: it is not stored anywhere, and closing this removes it for good.'),
    h('div', { class: 'acct-row' }, copy, close));
  return capsule;
};

/** Calls whose secret is already in front of the person: one capsule per call, whoever else was handed the record. */
const shown = new WeakSet<CallRecord>();

export type KeeperContext = Pick<AppContext, 'session' | 'announce' | 'router' | 'records'>;

/**
 * One answered call, dealt with once, by whoever fired it (the record `Actions.run` hands back,
 * never a listener's copy): its shown-once secret goes to `show`, unless it was already shown,
 * and what it means for this tab's own key is applied unless that is already so (the session
 * already runs on the successor, or has already ended). True when this call showed the secret.
 */
export const settleAnswer = (ctx: KeeperContext, record: CallRecord, show: (secret: string, what: string) => void): boolean => {
  const effect = sessionEffectOf(record, ownKeyId(ctx.records.get(null, 'me')));
  if (effect?.kind === 'rotated' && ctx.session.key('A') !== effect.secret) {
    ctx.session.setKey('A', effect.secret);
    ctx.announce.assertive(`This tab's key was rotated: the session now runs on ${effect.id}.`);
    void ctx.records.refresh(null, 'me').catch(() => undefined);
  } else if (effect?.kind === 'ended' && ctx.session.key('A') !== null) {
    ctx.session.setKey('A', null);
    ctx.announce.assertive('This tab’s own key was revoked: the session has ended.');
    ctx.router.go({ name: 'connect' });
  }
  const s = secretOf(record);
  if (!s || shown.has(record)) return false;
  shown.add(record);
  show(s.secret, s.what);
  return true;
};

/** Put a capsule for `secret` at the top of `host`. */
export const showIn = (host: HTMLElement) => (secret: string, what: string): void => {
  host.prepend(secretCapsule(host.ownerDocument, secret, what, () => undefined));
};

/** Pretty JSON, clipped: the Wire and the Inspector never paint megabytes. */
export const pretty = (value: Json | undefined, max = 6000): string => {
  if (value === undefined) return '';
  const text = JSON.stringify(value, null, 2) ?? '';
  return text.length > max ? `${text.slice(0, max)}\n… (${text.length - max} more characters)` : text;
};

// -- lines -------------------------------------------------------------------------------------

export type WireSource = 'hand' | 'director' | 'scenario' | 'auto' | 'event' | 'presence' | 'link';
export const WIRE_SOURCES: readonly WireSource[] = ['hand', 'director', 'scenario', 'auto', 'event', 'presence', 'link'];

export interface WireLine {
  readonly id: string;
  readonly at: number;
  readonly source: WireSource;
  readonly kind: 'call' | 'frame';
  readonly world: string | null;
  /** Columns: what went (method + path, or the frame's type), the answer, who, and the tail. */
  readonly what: string;
  readonly status: string;
  readonly tone: Tone;
  readonly who: string;
  readonly tail: string;
  readonly op: string | null;
  /** The whole thing, scrubbed, for the expanded view. */
  readonly detail: Json;
  /** Calls only: the scrubbed record, for the Inspector. */
  readonly record: CallRecord | null;
}

export const sourceOf = (s: Source): WireSource => s.kind;

export const whoOf = (s: Source): string => {
  switch (s.kind) {
    case 'hand': return `hand · ${s.via}`;
    case 'director': return `Director · step ${s.step}`;
    case 'scenario': return `scenario · step ${s.step + 1}`;
    case 'auto': return 'auto';
  }
};

/** `https://api/v1/worlds/x/claims?date=…` → `/v1/worlds/x/claims?date=…` (a relative URL is kept). */
export const pathOf = (url: string): string => {
  const m = /^[a-z]+:\/\/[^/]+(\/.*)?$/i.exec(url);
  if (m) return m[1] ?? '/';
  return url.replace(/^\$SB_API/, '');
};

const worldOfArgs = (args: CallRecord['args']): string | null => (typeof args.w === 'string' ? args.w : null);

export const lineOfRecord = (record: CallRecord, known: readonly (string | null | undefined)[] = []): WireLine => {
  const clean = scrubRecord(record, known);
  const answer = clean.answer;
  const v = answer ? answerOf(answer, record.via) : null;
  const body = v?.body;
  const seq = isObj(body) && typeof body.commitSeq === 'number' ? `#${body.commitSeq}` : '';
  const head = answer?.head ? answer.head.slice(0, 6) : '';
  const status = answer === null || v === null ? '…' : answer.status === 0 ? 'unreachable'
    : `${v.rpc ? 'rpc error' : v.status} ${answer.ms}ms${v.mcp === null ? '' : ` · MCP ${v.mcp}`}`;
  return {
    id: `call:${record.id}`,
    at: record.sentAt,
    source: sourceOf(record.source),
    kind: 'call',
    world: worldOfArgs(record.args),
    what: `${clean.twins.rest.method} ${pathOf(clean.twins.rest.url)}`,
    status,
    tone: v === null ? 'pending' : answer?.status === 0 ? 'error' : v.tone,
    who: `${whoOf(record.source)}${record.persona === 'A' ? '' : ` · ${record.persona}`}${record.via === 'mcp' ? ' · MCP' : ''}`,
    tail: [seq, head, v?.detail ?? ''].filter(Boolean).join(' '),
    op: record.op,
    detail: { op: record.op, args: clean.args as Json, answer: answer === null ? null : { status: answer.status, ms: answer.ms, head: answer.head, body: answer.body } },
    record: clean,
  };
};

/** A few words on what an event carried: who and which slots, or the op of a commit. */
export const eventSummary = (e: WorldEvent): string => {
  const d = e.data;
  const parts: string[] = [];
  if (typeof d.op === 'string') parts.push(d.op);
  if (typeof d.who === 'string' || typeof d.who === 'number') parts.push(String(d.who));
  if (typeof d.relay === 'string') parts.push(d.relay);
  if (typeof d.act === 'string') parts.push(d.act);
  if (typeof d.token === 'string') parts.push(`${d.token.slice(0, 4)}…`);
  if (Array.isArray(d.slots)) parts.push(`[${d.slots.join(',')}]`);
  else if (typeof d.slots === 'string') parts.push(d.slots);
  if (Array.isArray(d.commits)) parts.push(`${d.commits.length} commit${d.commits.length === 1 ? '' : 's'}`);
  if (d.truncated === true) parts.push('truncated');
  return parts.join(' ');
};

let frameSeq = 0;
const frameId = (): string => `frame:${(frameSeq += 1)}`;

export const lineOfEvent = (world: string, e: WorldEvent, known: readonly (string | null | undefined)[] = []): WireLine => ({
  id: frameId(),
  at: e.at,
  source: 'event',
  kind: 'frame',
  world,
  what: e.type,
  status: e.commitSeq === null ? '' : `#${e.commitSeq}`,
  tone: 'info',
  who: 'watch',
  tail: redactText(`${eventSummary(e)} ${e.head ? e.head.slice(0, 6) : ''}`.trim(), known),
  op: null,
  detail: redactJson(e as unknown as Json, known),
  record: null,
});

export const lineOfPresence = (world: string, f: PresenceOut, nameOf: (sid: string) => string, at: number): WireLine => {
  let who = '';
  let tail = '';
  switch (f.type) {
    case 'presence.roster': who = f.you.name; tail = `${f.peers.length} peer${f.peers.length === 1 ? '' : 's'}`; break;
    case 'presence.joined': who = f.peer.name; tail = 'joined'; break;
    case 'presence.left': who = nameOf(f.sid); tail = 'left'; break;
    case 'presence.full': tail = 'presence is full here; watching without it'; break;
    case 'presence': {
      who = `${nameOf(f.sid)} (unverified)`;
      const fr = f.frame;
      tail = fr.t === 'lift' ? `lift ${fr.what} ${fr.label}${fr.op ? ` · ${fr.op}` : ''}`
        : fr.t === 'act' ? `act ${fr.ph} ${fr.op}${fr.status ? ` ${fr.status}` : ''}`
          : fr.t === 'director' ? `director ${fr.ph}${fr.tool ? ` ${fr.tool}` : ''}`
            : fr.t === 'end' ? `end ${fr.how}`
              : fr.t === 'focus' ? `focus ${fr.view}`
                : fr.t;
      break;
    }
  }
  return {
    id: frameId(), at, source: 'presence', kind: 'frame', world, what: f.type, status: '', tone: 'info',
    who: redactText(who), tail: redactText(tail), op: null, detail: redactJson(f as unknown as Json), record: null,
  };
};

export const lineOfHello = (world: string, d: WorldDescribe, at: number): WireLine => ({
  id: frameId(), at, source: 'link', kind: 'frame', world, what: 'hello', status: `seq ${d.seq}`, tone: 'info',
  who: 'watch', tail: `${d.preset} · head ${d.head.slice(0, 6)}`, op: null, detail: d as unknown as Json, record: null,
});

/** A tap's frame, as it came: its type and a clipped, scrubbed line of it (the whole, parsed and scrubbed, when expanded). */
export const lineOfTap = (world: string, e: TapEvent, at: number, known: readonly (string | null | undefined)[] = []): WireLine => {
  if (e.kind !== 'frame') {
    const open = e.kind === 'open';
    return {
      id: frameId(), at, source: 'link', kind: 'frame', world, what: open ? 'tap open' : 'tap closed', status: open ? '101' : String(e.code),
      tone: open ? 'ok' : 'info', who: 'tap', tail: open ? 'raw watch socket, opened with the ticket' : redactText(e.why || 'closed', known),
      op: 'watch', detail: e as unknown as Json, record: null,
    };
  }
  let parsed: Json = e.text;
  try { parsed = JSON.parse(e.text) as Json; } catch { /* a frame that is not JSON is shown as text */ }
  const type = isObj(parsed) && typeof parsed.type === 'string' ? parsed.type : 'frame';
  return {
    id: frameId(), at, source: 'link', kind: 'frame', world, what: `tap · ${type}`, status: '', tone: 'info', who: 'tap',
    tail: redactText(e.text.length > 160 ? `${e.text.slice(0, 159)}…` : e.text, known), op: 'watch', detail: redactJson(parsed, known), record: null,
  };
};

export const lineOfState = (world: string, s: LinkState, why: string | undefined, at: number): WireLine => ({
  id: frameId(), at, source: 'link', kind: 'frame', world, what: `link ${s}`, status: '', tone: s === 'live' ? 'ok' : s === 'closed' ? 'error' : 'pending',
  who: 'socket', tail: why ? redactText(why) : '', op: null, detail: { state: s, why: why ?? null }, record: null,
});

// -- the log and its filter ----------------------------------------------------------------------

export interface WireFilter {
  readonly sources: ReadonlySet<WireSource>;
  readonly text: string;
  readonly outcome: 'all' | 'ok' | 'refused' | 'error' | 'pending';
  readonly world: string | null;
}

export const ALL_SOURCES: ReadonlySet<WireSource> = new Set(WIRE_SOURCES);
export const openFilter = (): WireFilter => ({ sources: ALL_SOURCES, text: '', outcome: 'all', world: null });

export const matches = (line: WireLine, f: WireFilter): boolean => {
  if (!f.sources.has(line.source)) return false;
  if (f.world !== null && line.world !== f.world) return false;
  if (f.outcome !== 'all' && line.tone !== f.outcome) return false;
  const q = f.text.trim().toLowerCase();
  if (!q) return true;
  return [line.what, line.status, line.who, line.tail, line.op ?? '', line.world ?? ''].some((s) => s.toLowerCase().includes(q));
};

export const filterLines = (lines: readonly WireLine[], f: WireFilter): WireLine[] => lines.filter((l) => matches(l, f));

/** Newest last, bounded; a call's `answered` line replaces its `sent` line. */
export class WireLog {
  private lines: WireLine[] = [];

  constructor(readonly cap = 400) {}

  add(line: WireLine): void {
    for (let i = this.lines.length - 1; i >= Math.max(0, this.lines.length - 200); i -= 1) {
      if (this.lines[i]!.id === line.id) {
        this.lines[i] = line;
        return;
      }
    }
    this.lines.push(line);
    if (this.lines.length > this.cap) this.lines.splice(0, this.lines.length - this.cap);
  }

  all(): readonly WireLine[] {
    return this.lines;
  }

  counts(): Record<WireSource, number> {
    const out = Object.fromEntries(WIRE_SOURCES.map((s) => [s, 0])) as Record<WireSource, number>;
    for (const l of this.lines) out[l.source] += 1;
    return out;
  }

  clear(): void {
    this.lines = [];
  }
}

/** This tab's own secrets: masked wherever they turn up, even in a shape the patterns miss. */
export const knownSecrets = (ctx: Pick<AppContext, 'session'>): (string | null)[] => {
  try {
    return [ctx.session.key('A'), ctx.session.key('B'), ctx.session.key('C'), ctx.session.operatorKey(), ctx.session.anthropicKey?.() ?? null];
  } catch {
    return [];
  }
};

// -- the screen ----------------------------------------------------------------------------------

const SOURCE_LABEL: Record<WireSource, string> = {
  hand: 'hand', director: 'Director', scenario: 'scenario', auto: 'auto', event: 'events', presence: 'presence', link: 'socket',
};

const clock = (ms: number): string => new Date(ms).toISOString().slice(11, 23);

export const mount: Screen = (root, ctx) => {
  const log = new WireLog();
  let filter = openFilter();
  let paused = false;
  let open: string | null = null;
  const offs: Unsub[] = [];

  const list = h('ol', { class: 'wire-lines', 'aria-live': 'off' });
  const counts = h('span', { class: 'wire-count' });
  const chips = h('div', { class: 'wire-chips', role: 'group', 'aria-label': 'Show sources' });
  const search = h('input', { class: 'wire-search', type: 'search', placeholder: 'filter: op, world, status…', 'aria-label': 'Filter the wire' });
  const outcome = h('select', { class: 'wire-outcome', 'aria-label': 'Answers' },
    ...(['all', 'ok', 'refused', 'error', 'pending'] as const).map((o) => h('option', { value: o }, o)));
  const pause = h('button', { class: 'wire-btn', type: 'button', 'aria-pressed': 'false' }, 'pause');
  const clear = h('button', { class: 'wire-btn', type: 'button' }, 'clear');
  // Closed, the Wire is one live line along the bottom edge; opened (the line, or W), the whole log.
  const ticker = h('button', { class: 'wire-ticker', type: 'button', 'aria-expanded': 'false', 'aria-label': 'The Wire: open the log (W)' });
  const body = h('div', { class: 'wire-body' });
  const section = h('section', { class: 'wire', 'aria-label': 'Wire' }, ticker, body);
  let opened = false;
  let lastTicked: string | null = null;

  const drawTicker = (): void => {
    const all = log.all();
    const last = all[all.length - 1];
    if (last?.id === lastTicked && ticker.childElementCount > 0) return;
    lastTicked = last?.id ?? null;
    put(ticker,
      h('span', { class: 'wire-eyebrow' }, 'WIRE'),
      last ? h('span', { class: `wire-now t-${last.tone} s-${last.source}` },
        h('span', { class: 'wire-dot', 'aria-hidden': 'true' }, last.kind === 'call' ? '●' : '⇠'),
        h('span', { class: 'wire-what' }, last.what),
        h('span', { class: 'wire-status' }, last.status),
        h('span', { class: 'wire-who' }, last.who),
        last.world ? h('span', { class: 'wire-world' }, last.world) : null,
        h('span', { class: 'wire-tail' }, last.tail)) : h('span', { class: 'wire-now' }, 'quiet: no calls yet'),
      h('span', { class: 'wire-count' }, String(all.length)),
      h('span', { class: 'wire-chev', 'aria-hidden': 'true' }, opened ? '▾' : '▴'));
  };

  let queued = false;
  const draw = (): void => {
    queued = false;
    drawTicker();
    if (!opened) return;
    const shown = filterLines(log.all(), filter).slice(-160);
    const c = log.counts();
    counts.textContent = `${shown.length} of ${log.all().length}`;
    put(chips, ...WIRE_SOURCES.map((s) => h('button', {
      class: `wire-chip s-${s}`, type: 'button', 'aria-pressed': String(filter.sources.has(s)),
      onclick: () => {
        const next = new Set(filter.sources);
        if (next.has(s)) next.delete(s); else next.add(s);
        filter = { ...filter, sources: next };
        schedule();
      },
    }, `${SOURCE_LABEL[s]} ${c[s]}`)));
    put(list, ...shown.map((line) => {
      const expanded = open === line.id;
      return h('li', { class: `wire-line t-${line.tone} s-${line.source}${expanded ? ' open' : ''}` },
        h('button', {
          class: 'wire-row', type: 'button', 'aria-expanded': String(expanded),
          onclick: () => {
            open = expanded ? null : line.id;
            if (line.record && !expanded) ctx.inspect({ kind: 'call', record: line.record }, { open: true });
            schedule();
          },
        },
        h('span', { class: 'wire-dot', 'aria-hidden': 'true' }, line.kind === 'call' ? '●' : '⇠'),
        h('time', { class: 'wire-at' }, clock(line.at)),
        h('span', { class: 'wire-what' }, line.what),
        h('span', { class: 'wire-status' }, line.status),
        h('span', { class: 'wire-who' }, line.who),
        line.world ? h('span', { class: 'wire-world' }, line.world) : null,
        h('span', { class: 'wire-tail' }, line.tail)),
        expanded ? h('pre', { class: 'wire-detail' }, pretty(line.detail)) : null);
    }));
    // Follow the newest line inside the list only; the page itself never scrolls for the Wire.
    if (!paused && open === null) list.scrollTop = list.scrollHeight;
  };
  const schedule = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(draw);
  };
  const add = (line: WireLine): void => {
    if (paused) return;
    log.add(line);
    schedule();
  };

  search.addEventListener('input', () => { filter = { ...filter, text: search.value }; schedule(); });
  outcome.addEventListener('change', () => { filter = { ...filter, outcome: outcome.value as WireFilter['outcome'] }; schedule(); });
  pause.addEventListener('click', () => { paused = !paused; pause.setAttribute('aria-pressed', String(paused)); pause.textContent = paused ? 'resume' : 'pause'; });
  clear.addEventListener('click', () => { log.clear(); open = null; schedule(); });

  // Calls: every record the Client sees, from any source.
  offs.push(ctx.client.on((record) => add(lineOfRecord(record, knownSecrets(ctx)))));

  // Frames: whichever sockets are open for Worlds this tab has touched.
  const known = new Set<string>();
  const tapped = new Map<string, { link: Link; offs: Unsub[] }>();
  const nameOf = (world: string) => (sid: string): string => ctx.roster.peers(world).find((p) => p.sid === sid)?.name ?? sid;
  const tap = (): void => {
    const focus = ctx.focus().world;
    if (focus) known.add(focus);
    const route = ctx.router.current();
    if ('world' in route) known.add(route.world);
    for (const r of ctx.roster.all()) known.add(r.world);
    for (const w of known) {
      const link = ctx.links.get(w);
      const had = tapped.get(w);
      if (had && had.link === link) continue;
      had?.offs.forEach((off) => off());
      tapped.delete(w);
      if (!link) continue;
      tapped.set(w, {
        link,
        offs: [
          link.onEvent((e) => add(lineOfEvent(w, e, knownSecrets(ctx)))),
          link.onPresence((f) => add(lineOfPresence(w, f, nameOf(w), Date.now()))),
          link.onHello((d) => add(lineOfHello(w, d, Date.now()))),
          link.onState((s, why) => add(lineOfState(w, s, why, Date.now()))),
        ],
      });
    }
  };
  offs.push(ctx.client.on((record) => { const w = worldOfArgs(record.args); if (w && !known.has(w)) { known.add(w); tap(); } }));
  // Taps a hand opened with a ticket it minted: their raw frames, as they came.
  offs.push(ctx.taps.on((w, e) => add(lineOfTap(w, e, Date.now(), knownSecrets(ctx)))));
  offs.push(ctx.roster.on((w) => { known.add(w); tap(); }));
  const poll = setInterval(tap, 1500);
  tap();

  const setOpen = (on: boolean): void => {
    opened = on;
    section.toggleAttribute('data-open', on);
    ticker.setAttribute('aria-expanded', String(on));
    ticker.setAttribute('aria-label', on ? 'The Wire: close the log (W, Esc)' : 'The Wire: open the log (W)');
    body.inert = !on;
    lastTicked = null;
    draw();
  };
  ticker.addEventListener('click', () => setOpen(!opened));
  section.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !opened || e.defaultPrevented) return;
    e.preventDefault();
    setOpen(false);
    ticker.focus();
  });
  put(body,
    h('header', { class: 'wire-head' },
      h('span', { class: 'wire-eyebrow' }, 'WIRE'), counts, chips, search, outcome, pause, clear),
    list);
  put(root, section);
  setOpen(false);

  return () => {
    clearInterval(poll);
    offs.forEach((off) => off());
    for (const t of tapped.values()) t.offs.forEach((off) => off());
    root.replaceChildren();
  };
};
